import React, { PropsWithChildren } from 'react';
import styled from '@emotion/styled';
import MuiCardActions from '@mui/material/CardActions';
import MuiCardContent from '@mui/material/CardContent';
import MuiIconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';

import { Link as GatsbyLink } from 'gatsby';
import { black20, turquoise, white50, black50, white75 } from '~/styles';

export const CardContentHeader = styled(MuiCardActions)`
  align-items: flex-start;
  padding: 0;
` as React.FC<PropsWithChildren<React.ComponentProps<typeof MuiCardActions>>>;

export const Chip = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: absolute;
  top: 0;
  left: -10px;
  height: 27px;
  width: 85px;
  border-radius: 0 14px 14px 0;
  background: ${turquoise};
  color: white;
  padding-right: 20px;
  font-size: 12px;
  letter-spacing: 2px;
  text-transform: uppercase;
`;

export const BookmarkIconButton = styled.div`
  padding: 0;
  :hover {
    background: none;
  }
` as React.FC<PropsWithChildren<React.ComponentProps<typeof MuiIconButton>>>;

export const Author = styled.span`
  display: flex;
  align-items: center;
`;

export const AuthorNameDark = styled.span`
  color: #0009;
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
`;

export const ClassImageOverlay = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;
export const MeditationImageOverlay = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0 12px 0 16px;
`;

export const OverlayContent = styled.div``;

export const BorderLinearProgress = styled(LinearProgress)`
  height: 4;
  background: rgba(45, 179, 198, 0.3);
  .MuiLinearProgress-barColorPrimary {
    background: ${turquoise};
    border-radius: 20;
  }
` as React.FC<PropsWithChildren<React.ComponentProps<typeof LinearProgress>>>;

export const ProgressBarWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
`;

export const LevelTitle = styled.p`
  max-width: 100%;
  color: ${turquoise};
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 2px;
  margin: 0 0 0 9px;
  text-transform: uppercase;
`;
export const CardContentDescriptions = styled.div`
  display: none;
  opacity: 0;
  transition: 0.3s all ease;
  color: #00000099;
  font-size: 14px;
  line-height: 20px;
  font-weight: 200;
  margin-bottom: 18px;
  letter-spacing: 0.25px;
`;

export const CardContentTitle = styled.div`
  margin: 3px 0 8px;
  color: #000000de;
  font-size: 14px;
  line-height: 24px;
  font-weight: 600;
  max-width: 254px;
  letter-spacing: 0.1px;
`;

export const CardContent = styled(MuiCardContent)`
  display: flex;
  flex-direction: column;
  padding: 8px 12px 6px 16px;
` as React.FC<PropsWithChildren<React.ComponentProps<typeof MuiCardContent>>>;

export const CardGradientOverlay = styled.div`
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  background-image: linear-gradient(to bottom, ${black20}, transparent);
  background-image: -webkit-linear-gradient(to bottom, ${black20}, transparent);
  background-image: -moz-linear-gradient(to bottom, ${black20}, transparent);
`;

export const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 100%;
  width: 30px;
  height: 30px;
  background-color: ${white75};
`;

export const ArticleCardContent = styled(MuiCardContent)`
  display: flex;
  flex-direction: column;
  padding: 8px 16px;
` as React.FC<PropsWithChildren<React.ComponentProps<typeof MuiCardContent>>>;

export const ArticleGradientOverlay = styled.div`
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  background-image: linear-gradient(to bottom, ${black20}, transparent);
  background-image: -webkit-linear-gradient(to bottom, ${black20}, transparent);
  background-image: -moz-linear-gradient(to bottom, ${black20}, transparent);
`;

export const TextStandoutGradientOverlay = styled.div`
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  background-image: linear-gradient(to bottom, ${white50}, ${black50}),
    linear-gradient(to bottom, black, black);
  background-image: -webkit-linear-gradient(to bottom, ${white50}, ${black50}),
    -webkit-linear-gradient(to bottom, black, black);
  background-image: -moz-linear-gradient(to bottom, ${white50}, ${black50}),
    -moz-linear-gradient(to bottom, black, black);
  opacity: 0.3;
`;

export const MediaPlayIcon = styled.div<{ hoverText: () => string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.75);
  width: auto;
  border-radius: 15px;
  :hover {
    ::before {
      transition: 0.3s max-width ease-in-out, 0s 0s margin-left;
      max-width: 115px;
      margin-left: 14px;
    }
  }
  ::before {
    margin-left: 0px;
    content: '${({ hoverText }) => hoverText()}';
    font-size: 12px;
    font-weight: 700;
    transition: 0.3s max-width ease-in-out, 0.1s 0.1s margin-left;
    white-space: nowrap;
    max-width: 0px;
    overflow: hidden;
  }
`;

export const CardButtonsContainer = styled.div`
  position: absolute;
  width: 100%;
  padding: 0 8px 8px;
  bottom: 0;
  z-index: 9;
  display: grid;
  grid-template-columns: min-content auto min-content;
  grid-template-areas: 'burger . play-button';
  .burger-btn {
    grid-area: burger;
  }
  .play-btn {
    grid-area: play-button;
  }
`;

export const Link = styled(GatsbyLink)<PropsWithChildren<React.ComponentProps<typeof GatsbyLink>>>`
  :hover {
    text-decoration: none;
  }
`;
